import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const ActionFormModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editAction = null,
  defaultPeriod = 30 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    responsible: '',
    period: defaultPeriod,
    budget: '',
    dependencies: [],
    kpis: [''],
    progress: 0
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editAction) {
      setFormData({
        ...editAction,
        kpis: editAction?.kpis || ['']
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        responsible: '',
        period: defaultPeriod,
        budget: '',
        dependencies: [],
        kpis: [''],
        progress: 0
      });
    }
    setErrors({});
  }, [editAction, defaultPeriod, isOpen]);

  const categoryOptions = [
    { value: 'supply', label: 'Mitigasi Pasokan' },
    { value: 'operational', label: 'Perbaikan Operasional' },
    { value: 'equipment', label: 'Pengadaan Peralatan' },
    { value: 'financial', label: 'Optimasi Keuangan' },
    { value: 'market', label: 'Ekspansi Pasar' },
    { value: 'governance', label: 'Tata Kelola' },
    { value: 'hr', label: 'Sumber Daya Manusia' }
  ];

  const priorityOptions = [
    { value: 'critical', label: 'Kritis' },
    { value: 'high', label: 'Tinggi' },
    { value: 'medium', label: 'Sedang' },
    { value: 'low', label: 'Rendah' }
  ];

  const periodOptions = [
    { value: 30, label: '30 Hari' },
    { value: 60, label: '60 Hari' },
    { value: 90, label: '90 Hari' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleKPIChange = (index, value) => {
    const newKPIs = [...formData?.kpis];
    newKPIs[index] = value;
    setFormData(prev => ({ ...prev, kpis: newKPIs }));
  };

  const addKPI = () => {
    setFormData(prev => ({ ...prev, kpis: [...prev?.kpis, ''] }));
  };

  const removeKPI = (index) => {
    if (formData?.kpis?.length > 1) {
      const newKPIs = formData?.kpis?.filter((_, idx) => idx !== index);
      setFormData(prev => ({ ...prev, kpis: newKPIs }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.title?.trim()) newErrors.title = 'Judul aksi wajib diisi';
    if (!formData?.description?.trim()) newErrors.description = 'Deskripsi wajib diisi';
    if (!formData?.category) newErrors.category = 'Kategori wajib dipilih';
    if (!formData?.responsible?.trim()) newErrors.responsible = 'Penanggung jawab wajib diisi';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate()) {
      const cleanedKPIs = formData?.kpis?.filter(kpi => kpi?.trim() !== '');
      onSave({
        ...formData,
        kpis: cleanedKPIs,
        budget: formData?.budget ? parseFloat(formData?.budget) : null,
        id: editAction?.id || Date.now()
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {editAction ? 'Edit Aksi' : 'Tambah Aksi Baru'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          <Input
            label="Judul Aksi"
            type="text"
            placeholder="Contoh: Implementasi sistem monitoring pasokan real-time"
            value={formData?.title}
            onChange={(e) => handleChange('title', e?.target?.value)}
            error={errors?.title}
            required
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Deskripsi Detail <span className="text-error">*</span>
            </label>
            <textarea
              className="w-full min-h-[100px] px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-3 focus:ring-ring transition-all"
              placeholder="Jelaskan detail implementasi, langkah-langkah, dan hasil yang diharapkan..."
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
            />
            {errors?.description && (
              <p className="text-sm text-error">{errors?.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Kategori"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleChange('category', value)}
              error={errors?.category}
              required
              placeholder="Pilih kategori"
            />

            <Select
              label="Prioritas"
              options={priorityOptions}
              value={formData?.priority}
              onChange={(value) => handleChange('priority', value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Penanggung Jawab"
              type="text"
              placeholder="Nama PIC"
              value={formData?.responsible}
              onChange={(e) => handleChange('responsible', e?.target?.value)}
              error={errors?.responsible}
              required
            />

            <Select
              label="Periode Target"
              options={periodOptions}
              value={formData?.period}
              onChange={(value) => handleChange('period', value)}
              required
            />
          </div>

          <Input
            label="Budget (IDR)"
            type="number"
            placeholder="Contoh: 50000000"
            value={formData?.budget}
            onChange={(e) => handleChange('budget', e?.target?.value)}
            description="Kosongkan jika tidak memerlukan budget"
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                KPI Target
              </label>
              <Button
                type="button"
                variant="outline"
                size="xs"
                iconName="Plus"
                iconPosition="left"
                onClick={addKPI}
              >
                Tambah KPI
              </Button>
            </div>
            <div className="space-y-2">
              {formData?.kpis?.map((kpi, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={`KPI ${index + 1}`}
                    value={kpi}
                    onChange={(e) => handleKPIChange(index, e?.target?.value)}
                    className="flex-1"
                  />
                  {formData?.kpis?.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeKPI(index)}
                    >
                      <Icon name="Trash2" size={18} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </form>

        <div className="flex gap-3 p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="default"
            onClick={handleSubmit}
            fullWidth
          >
            {editAction ? 'Simpan Perubahan' : 'Tambah Aksi'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActionFormModal;